import React, { useCallback, useEffect, useState } from "react";

import {
    Card,
    Row,
    Table,
    Col,
    Button,
    Form,
    Alert,
    Badge,
    Spinner,
    Pagination,
} from "react-bootstrap";
import { useTable, useRowSelect } from "react-table";
import Select from "react-select";

import { addDefaultOption } from "../../../utils/utility";
import SearchEnrolment from "../CommonComponents/SearchEnrolment";
import AllocatedStudents from "./AllocatedStudents";
import api from "../../../service/api";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFlagCheckered } from "@fortawesome/free-solid-svg-icons";
import {
    INITIAL_KIV_MODAL_INFO,
    StatusSummaryTable,
} from "../../../components/KivSummaryStatusTable";
import { ConfirmModal } from "../../../components/ConfirmModal";

const IndeterminateCheckbox = React.forwardRef(
    ({ indeterminate, ...rest }, ref) => {
        const defaultRef = React.useRef();
        const resolvedRef = ref || defaultRef;

        React.useEffect(() => {
            resolvedRef.current.indeterminate = indeterminate;
        }, [resolvedRef, indeterminate]);

        return (
            <>
                <input type="checkbox" ref={resolvedRef} {...rest} />
            </>
        );
    }
);

const PendingStudentList = ({
    columns,
    data,
    getPendingStudentsApi,
    pendingSearch,
    setPendingSearch,
    batchLists,
    batch,
    getBatchDetail,
    setRerender,
    rerender,
    setNotif,
    paginationMeta = {},
    paginationLinks = {},
    handlePaginationChange,
}) => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const path = urlParams.get("status");
    const batchIdParam = urlParams.get("batchId");
    const batchName = urlParams.get("batchName");

    let redirectPath = "";
    let selectedBatchName = "";

    if (path && batchIdParam && batchName) {
        redirectPath = `/tms/batches/${path}`;
        selectedBatchName = batchName;
    }

    const [loading, setLoading] = useState(false);
    const [selectedBatchId, setSelectedBatchId] = useState(batchIdParam);
    const [defaultConfig, setDefaultConfig] = useState();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [addingStudents, setAddingStudents] = useState(false);

    const permissionData = useSelector((state) =>
        state.user ? state.user : state
    );

    const permission = permissionData?.data?.permissions;

    const [error, setError] = useState();
    const [customStatusOptions, setCustomStatusOptions] = useState([]);
    const [customStatus, setCustomStatus] = useState();
    const [customStatusRemarks, setCustomStatusRemarks] = useState();

    const [statusModalInfo, setStatusModalInfo] = useState({
        size: "sm",
        notifMsg: "",
        open: false,
        severity: "danger",
        static: false,
    });

    const searchFields = {
        fin: "",
        date: "",
        registration_type: "",
        type: "",
        trade_category: "",
        type_of_trade: "",
        application_option: "",
        course_id: "",
        company_id: "",
    };

    const resetSearch = () => {
        setPendingSearch(searchFields);
        getPendingStudentsApi(1, searchFields);
    };

    const addToBatch = async () => {
        try {
            setLoading(true);
            setIsSubmitting(true);
            setAddingStudents(true);
            const selectedStudents = selectedFlatRows.map((d) => d.original);
            await api.post(`tms/batches/${selectedBatchId}/batch-students`, {
                students: selectedStudents,
            });

            await getPendingStudentsApi(1);
            setLoading(false);
            setAddingStudents(false);
            setNotif({
                notifMsg: "Added student/s to class",
                open: true,
                severity: "success",
            });
            getBatchDetail();
        } catch (error) {
            setNotif({
                notifMsg: error?.response?.data?.message,
                open: true,
                severity: "warning",
            });
            setIsSubmitting(false);
            setAddingStudents(false);
        }
    };

    const batchListArray = batchLists?.map((item, index) => {
        return { value: item?.id, label: `${item.name} - ${item.test_date}` };
    });

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        selectedFlatRows,
    } = useTable(
        {
            columns,
            data,
        },
        useRowSelect,
        (hooks) => {
            hooks.visibleColumns.push((columns) => [
                {
                    id: "selection",
                    Header: ({ getToggleAllRowsSelectedProps }) => (
                        <div>
                            <IndeterminateCheckbox
                                {...getToggleAllRowsSelectedProps()}
                            />
                        </div>
                    ),
                    Cell: ({ row }) => (
                        <div>
                            <IndeterminateCheckbox
                                {...row.getToggleRowSelectedProps()}
                            />
                        </div>
                    ),
                },
                ...columns,
            ]);
        }
    );

    const getConfig = useCallback(async () => {
        const configResponse = await api.get(`tms/enrolments/config`, {});
        setDefaultConfig(configResponse.data);
    }, []);

    useEffect(() => {
        getConfig();
    }, [getConfig]); //  eslint-disable-line react-hooks/exhaustive-deps

    const batchAllocatedStudentTableColumns = [
        {
            Header: "S.no",
        },
        {
            Header: "Name",
            accessor: (data) => {
                return <>{data?.name}</>;
            },
        },
        {
            Header: "FIN",
            accessor: (data) => {
                return <>{data?.fin || "-"}</>;
            },
        },
        {
            Header: "Sponsor company",
            id: "sponsor_company",
            accessor: (data) => {
                return (
                    <div>
                        {data?.sponsor_company}
                        {!data?.sponsor_company && (
                            <Badge bg="primary">SELF</Badge>
                        )}
                    </div>
                );
            },
        },
        {
            Header: "Type of trade",
            id: "data.type_of_trade",
            accessor: (data) => {
                return <>{data?.type_of_trade || "-"}</>;
            },
        },
        {
            Header: "Application option",
            id: "data.enrolment.application_option",
            accessor: (data) => {
                return <>{data?.application_option || "-"}</>;
            },
        },
    ];

    const popConfirmationModal = () => {
        if (!customStatus) {
            setNotif({
                notifMsg: "Please select a custom status",
                open: true,
                severity: "danger",
            });

            return;
        }

        setStatusModalInfo({
            ...INITIAL_KIV_MODAL_INFO,
            open: true,
            size: "lg",
            severity: "info",
            notifMsg: (
                <StatusSummaryTable
                    rows={selectedFlatRows.map((row) => ({
                        enrolment_id: row.original.enrolment_id,
                        name: row.original.name,
                        fin: row.original.fin,
                        old_status: row.original.status_key,
                        new_status: customStatus
                    }))}
                    
                />
            ),
        });
    };


    const customUpdateStatus = async () => {
        setIsSubmitting(true);

        try {
            const payload = {
                ids: selectedFlatRows.map((row) => row.original.id),
                status: customStatus,
                remarks: customStatusRemarks,
            };

            const response = await api.post(
                "tms/enrolments/update-custom-status",
                payload
            );

            if (response.status) {
                setStatusModalInfo({
                    open: true,
                    notifMsg: <StatusSummaryTable rows={response?.data}/>,
                    severity: "success",
                    values: null,
                    size: "lg",
                    isStatic: true,
                    refresh: true,
                });
            }

            setNotif({
                notifMsg: response?.message,
                open: true,
                severity: response.status ? "success" : "danger",
            });
        } catch (error) {
            console.error(error);

            setNotif({
                notifMsg: "Something went wrong!",
                open: true,
                severity: "danger",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const getStatusResultOptions = useCallback(async () => {
        try {
            const result = await api.get(
                "tms/enrolments/get-config-result-enrolments"
            );

            setCustomStatusOptions(result.data?.options);
        } catch (error) {
            console.error(error);

            setNotif({
                notifMsg: "Error in retrieving options",
                open: true,
                severity: "error",
            });
        }
    }, [setNotif]);

    useEffect(() => {
        getStatusResultOptions();
    }, [getStatusResultOptions]);

    return (
        <React.Fragment>
            <Card.Header>
                <Row>
                    <Col md={12}>
                        <SearchEnrolment
                            search={getPendingStudentsApi}
                            setSearchValue={setPendingSearch}
                            searchValue={pendingSearch}
                            defaultConfig={defaultConfig}
                            resetSearch={resetSearch}
                            isPendingStudents={true}
                        />
                    </Col>
                </Row>
                <br />
                <Row>
                    <Col md={6}>
                        <Table bordered className="mb-4" size="sm">
                            <tbody>
                                <tr>
                                    <th>Name:</th>
                                    <td>{batch?.name || "-"}</td>
                                </tr>
                                <tr>
                                    {/* {console.log("THIS IS BATCH: ", batch)} */}
                                    <th>Test venue:</th>
                                    <td>
                                        {batch?.test_venue?.join(", ") || "-"}
                                    </td>
                                </tr>
                                <tr>
                                    <th>Training venue:</th>
                                    <td>
                                        {batch?.training_venue?.join(", ") ||
                                            "-"}
                                    </td>
                                </tr>
                                <tr>
                                    <th>Test date:</th>
                                    <td>{batch?.test_date || "-"}</td>
                                </tr>
                                <tr>
                                    <th>Status:</th>
                                    <td>
                                        {batch?.status ? (
                                            <Badge bg="primary">
                                                {batch?.status}
                                            </Badge>
                                        ) : (
                                            "-"
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            </Card.Header>

            {loading && <div>Loading...</div>}
            {/* {console.log(batch)} */}
            {batch.enrolment && (
                <AllocatedStudents
                    batch={batch}
                    columns={batchAllocatedStudentTableColumns}
                    data={batch.enrolment}
                    setRerender={setRerender}
                    rerender={rerender}
                    setNotif={setNotif}
                />
            )}

            <Card.Body className="pt-0">
                <Col md="10">
                    <h1 className="h3 mb-3">Candidates</h1>
                </Col>
                <>
                    {selectedFlatRows.length > 0 && (
                        <>
                            <hr />
                            <Row>
                                {selectedBatchName === "" && (
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Select
                                                className="is-invalid react-select-container"
                                                classNamePrefix="react-select "
                                                options={addDefaultOption(
                                                    batchListArray
                                                )}
                                                onChange={(e) => {
                                                    setSelectedBatchId(
                                                        e?.value
                                                    );
                                                }}
                                                disabled={isSubmitting}
                                                name="batch"
                                                isClearable={true}
                                            />
                                        </Form.Group>
                                    </Col>
                                )}
                                <Col md={6}>
                                    {!addingStudents && (
                                        <>
                                            <Button
                                                variant="info"
                                                onClick={addToBatch}
                                                className="mb-2"
                                                disabled={addingStudents}
                                            >
                                                Add student to class/batch{" "}
                                                {selectedBatchName !== "" &&
                                                    " - " + selectedBatchName}
                                            </Button>

                                            {permission?.includes(
                                                "ENROLLMENT-ACCESS-KIV"
                                            ) && (
                                                <Row className="align-items-end mb-4 g-3">
                                                    <Col xs={12} md={4}>
                                                        <Form.Label>
                                                            New Status
                                                        </Form.Label>

                                                        <Select
                                                            className="react-select-container"
                                                            classNamePrefix="react-select"
                                                            options={addDefaultOption(
                                                                customStatusOptions
                                                            )}
                                                            onChange={(
                                                                option
                                                            ) =>
                                                                setCustomStatus(
                                                                    option?.value
                                                                )
                                                            }
                                                            isDisabled={
                                                                isSubmitting
                                                            }
                                                            name="result_status"
                                                            isClearable
                                                            placeholder="Select custom status..."
                                                            styles={{
                                                                control: (
                                                                    base
                                                                ) => ({
                                                                    ...base,
                                                                    minHeight: 48,
                                                                }),
                                                            }}
                                                        />
                                                    </Col>

                                                    <Col xs={12} md={6}>
                                                        <Form.Label>
                                                            Remarks
                                                        </Form.Label>

                                                        <Form.Control
                                                            as="textarea"
                                                            rows={1}
                                                            size="lg"
                                                            name="remarks"
                                                            style={{
                                                                resize: "none",
                                                                minHeight: 48,
                                                            }}
                                                            onChange={(event) =>
                                                                setCustomStatusRemarks(
                                                                    event.target
                                                                        .value
                                                                )
                                                            }
                                                            placeholder="Enter remarks..."
                                                        />
                                                    </Col>

                                                    <Col xs={12} md={2}>
                                                        <Button
                                                            className="w-100 d-flex align-items-center justify-content-center"
                                                            variant="info"
                                                            size="lg"
                                                            style={{
                                                                minHeight: 48,
                                                            }}
                                                            onClick={
                                                                popConfirmationModal
                                                            }
                                                            disabled={
                                                                isSubmitting
                                                            }
                                                            title="Apply custom status"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={
                                                                    faFlagCheckered
                                                                }
                                                            />
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            )}
                                        </>
                                    )}

                                    {addingStudents && (
                                        <div className="my-3">
                                            <Spinner
                                                animation="border"
                                                role="status"
                                            >
                                                <span className="visually-hidden">
                                                    Loading...
                                                </span>
                                            </Spinner>
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </>
                    )}
                    {data.length === 0 && (
                        <Alert className="my-3" variant="warning">
                            <div className="alert-message">
                                No new enrolment list
                            </div>
                        </Alert>
                    )}
                    {data.length > 0 && (
                        <>
                            {paginationMeta &&
                                paginationMeta?.last_page &&
                                paginationMeta?.last_page > 1 && (
                                    <Row className="mb-3">
                                        <Col md="6">
                                            <span className="mx-2">
                                                Page{" "}
                                                <strong>
                                                    {
                                                        paginationMeta?.current_page
                                                    }{" "}
                                                    of{" "}
                                                    {paginationMeta?.last_page}
                                                </strong>
                                            </span>
                                        </Col>
                                        <Col md="6">
                                            <Pagination className="float-end mb-0">
                                                <Pagination.First
                                                    onClick={() =>
                                                        handlePaginationChange(
                                                            paginationMeta?.first
                                                        )
                                                    }
                                                    disabled={
                                                        paginationLinks?.prev ===
                                                        null
                                                    }
                                                />
                                                <Pagination.Prev
                                                    onClick={() =>
                                                        handlePaginationChange(
                                                            paginationMeta?.current_page -
                                                                1
                                                        )
                                                    }
                                                    disabled={
                                                        paginationLinks?.prev ===
                                                        null
                                                    }
                                                />
                                                <Pagination.Next
                                                    onClick={() =>
                                                        handlePaginationChange(
                                                            paginationMeta?.current_page +
                                                                1
                                                        )
                                                    }
                                                    disabled={
                                                        paginationLinks?.next ===
                                                        null
                                                    }
                                                />
                                                <Pagination.Last
                                                    onClick={() =>
                                                        handlePaginationChange(
                                                            paginationMeta?.last
                                                        )
                                                    }
                                                    disabled={
                                                        paginationLinks?.next ===
                                                        null
                                                    }
                                                />
                                            </Pagination>
                                        </Col>
                                    </Row>
                                )}
                            <Row>
                                <Table
                                    responsive
                                    striped
                                    bordered
                                    {...getTableProps()}
                                    size="md"
                                >
                                    <thead>
                                        {headerGroups.map((headerGroup) => (
                                            <tr
                                                {...headerGroup.getHeaderGroupProps()}
                                            >
                                                {headerGroup.headers.map(
                                                    (column) => (
                                                        <th
                                                            {...column.getHeaderProps()}
                                                        >
                                                            {column.render(
                                                                "Header"
                                                            )}
                                                        </th>
                                                    )
                                                )}
                                            </tr>
                                        ))}
                                    </thead>
                                    <tbody {...getTableBodyProps()}>
                                        {rows.map((row, idx) => {
                                            prepareRow(row);

                                            return (
                                                <tr {...row.getRowProps()}>
                                                    {row.cells.map(
                                                        (cell, index) => {
                                                            if (index === 1) {
                                                                return (
                                                                    <td
                                                                        key={
                                                                            index
                                                                        }
                                                                    >
                                                                        {paginationMeta?.current_page >
                                                                        1
                                                                            ? (paginationMeta?.current_page -
                                                                                  1) *
                                                                                  paginationMeta?.per_page +
                                                                              idx +
                                                                              1
                                                                            : idx +
                                                                              1}
                                                                    </td>
                                                                );
                                                            }
                                                            return (
                                                                <td
                                                                    {...cell.getCellProps()}
                                                                >
                                                                    {cell.render(
                                                                        "Cell"
                                                                    )}
                                                                </td>
                                                            );
                                                        }
                                                    )}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </Row>
                            {paginationMeta &&
                                paginationMeta?.last_page &&
                                paginationMeta?.last_page > 1 && (
                                    <Row className="mt-3">
                                        <Col md="6">
                                            <span className="mx-2">
                                                Page{" "}
                                                <strong>
                                                    {
                                                        paginationMeta?.current_page
                                                    }{" "}
                                                    of{" "}
                                                    {paginationMeta?.last_page}
                                                </strong>
                                            </span>
                                        </Col>
                                        <Col md="6">
                                            <Pagination className="float-end mb-0">
                                                <Pagination.First
                                                    onClick={() =>
                                                        handlePaginationChange(
                                                            paginationMeta?.first
                                                        )
                                                    }
                                                    disabled={
                                                        paginationLinks?.prev ===
                                                        null
                                                    }
                                                />
                                                <Pagination.Prev
                                                    onClick={() =>
                                                        handlePaginationChange(
                                                            paginationMeta?.current_page -
                                                                1
                                                        )
                                                    }
                                                    disabled={
                                                        paginationLinks?.prev ===
                                                        null
                                                    }
                                                />
                                                <Pagination.Next
                                                    onClick={() =>
                                                        handlePaginationChange(
                                                            paginationMeta?.current_page +
                                                                1
                                                        )
                                                    }
                                                    disabled={
                                                        paginationLinks?.next ===
                                                        null
                                                    }
                                                />
                                                <Pagination.Last
                                                    onClick={() =>
                                                        handlePaginationChange(
                                                            paginationMeta?.last
                                                        )
                                                    }
                                                    disabled={
                                                        paginationLinks?.next ===
                                                        null
                                                    }
                                                />
                                            </Pagination>
                                        </Col>
                                    </Row>
                                )}
                        </>
                    )}
                </>
            </Card.Body>

            <ConfirmModal
                modalInfo={statusModalInfo}
                setModalInfo={setStatusModalInfo}
                api={customUpdateStatus}
                refreshApi={resetSearch}
            />
        </React.Fragment>
    );
};

export default PendingStudentList;
